import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Fuel, X, ArrowDownCircle, ArrowUpCircle, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FuelPodsPage = () => {
  const [pods, setPods] = useState([]);
  const [events, setEvents] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePod, setShowCreatePod] = useState(false);
  const [showEventFor, setShowEventFor] = useState(null);

  const [podForm, setPodForm] = useState({
    label: "", capacity_litres: "", current_litres: "", location: "", low_threshold_litres: "", notes: "",
  });
  const [eventForm, setEventForm] = useState({
    event_type: "dispense", litres: "", machine_id: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, e, m] = await Promise.all([
        axios.get(`${API}/mcs/fuel-pods`),
        axios.get(`${API}/mcs/fuel-events`),
        axios.get(`${API}/machines`),
      ]);
      setPods(p.data || []);
      setEvents(e.data || []);
      setMachines(m.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createPod = async () => {
    if (!podForm.label || !podForm.capacity_litres) return toast.error("Label and capacity required");
    setSaving(true);
    try {
      await axios.post(`${API}/mcs/fuel-pods`, {
        ...podForm,
        capacity_litres: Number(podForm.capacity_litres),
        current_litres: Number(podForm.current_litres) || 0,
        low_threshold_litres: podForm.low_threshold_litres ? Number(podForm.low_threshold_litres) : null,
      });
      toast.success("Fuel pod created");
      setShowCreatePod(false);
      setPodForm({ label: "", capacity_litres: "", current_litres: "", location: "", low_threshold_litres: "", notes: "" });
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const createEvent = async (podId) => {
    if (!eventForm.litres || Number(eventForm.litres) <= 0) return toast.error("Litres required");
    setSaving(true);
    try {
      await axios.post(`${API}/mcs/fuel-events`, {
        fuel_pod_id: podId,
        event_type: eventForm.event_type,
        litres: Number(eventForm.litres),
        machine_id: eventForm.machine_id || null,
        notes: eventForm.notes || null,
      });
      toast.success(`${eventForm.event_type === "refill" ? "Refill" : "Dispense"} logged`);
      setShowEventFor(null);
      setEventForm({ event_type: "dispense", litres: "", machine_id: "", notes: "" });
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="fuel-pods-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900 font-heading">Fuel Pods</h1>
          <p className="text-sm text-slate-500 mt-1">Track on-site diesel pods. Refills and dispenses adjust the level live.</p>
        </div>
        <button onClick={() => setShowCreatePod(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold" data-testid="create-fuel-pod-btn">
          <Plus className="w-5 h-5" /> New Pod
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : pods.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-md text-slate-400">No fuel pods yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pods.map(p => {
            const pct = Math.max(0, Math.min(100, (Number(p.current_litres || 0) / Number(p.capacity_litres || 1)) * 100));
            const low = p.low_threshold_litres && Number(p.current_litres || 0) <= Number(p.low_threshold_litres);
            const podEvents = events.filter(e => e.fuel_pod_id === p.id).slice(0, 5);
            return (
              <div key={p.id} className="bg-white border-2 border-slate-200 rounded-md p-4" data-testid={`fp-card-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Fuel className={`w-5 h-5 ${low ? "text-red-500" : "text-orange-500"}`} />
                    <div>
                      <div className="font-bold text-slate-900">{p.label}</div>
                      <div className="text-xs text-slate-500">{p.location || "—"}</div>
                    </div>
                  </div>
                  {low && <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase"><AlertTriangle className="w-3 h-3" /> Low</span>}
                </div>
                <div className="mt-2 mb-1 flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-900">{Number(p.current_litres || 0).toFixed(0)} L</span>
                  <span className="text-slate-500">/ {Number(p.capacity_litres).toFixed(0)} L</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div className={`h-3 rounded-full ${low ? "bg-red-500" : pct < 40 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => { setEventForm({ event_type: "dispense", litres: "", machine_id: "", notes: "" }); setShowEventFor(p.id); }}
                    className="flex-1 px-3 py-2 border-2 border-orange-300 text-orange-700 hover:bg-orange-50 rounded text-xs font-semibold inline-flex items-center justify-center gap-1"
                    data-testid={`fp-dispense-${p.id}`}>
                    <ArrowDownCircle className="w-4 h-4" /> Dispense
                  </button>
                  <button onClick={() => { setEventForm({ event_type: "refill", litres: "", machine_id: "", notes: "" }); setShowEventFor(p.id); }}
                    className="flex-1 px-3 py-2 border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded text-xs font-semibold inline-flex items-center justify-center gap-1"
                    data-testid={`fp-refill-${p.id}`}>
                    <ArrowUpCircle className="w-4 h-4" /> Refill
                  </button>
                </div>
                {podEvents.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-2 text-[11px] space-y-1">
                    <div className="font-semibold uppercase text-slate-500 mb-1">Recent</div>
                    {podEvents.map(ev => (
                      <div key={ev.id} className="flex items-center justify-between text-slate-600">
                        <span>{ev.event_type === "refill" ? "↑" : "↓"} {Number(ev.litres).toFixed(0)}L {ev.machine_label ? `→ ${ev.machine_label}` : ""}</span>
                        <span className="text-slate-400">{ev.operator_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreatePod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">New Fuel Pod</h2>
              <button onClick={() => setShowCreatePod(false)}><X className="w-6 h-6 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <input placeholder="Label (e.g. Pod #1)" value={podForm.label} onChange={(e) => setPodForm({...podForm, label: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-label" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Capacity (L)" value={podForm.capacity_litres} onChange={(e) => setPodForm({...podForm, capacity_litres: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-capacity" />
                <input type="number" placeholder="Current (L)" value={podForm.current_litres} onChange={(e) => setPodForm({...podForm, current_litres: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-current" />
              </div>
              <input placeholder="Location (e.g. Job 1 yard)" value={podForm.location} onChange={(e) => setPodForm({...podForm, location: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <input type="number" placeholder="Low alert threshold (L)" value={podForm.low_threshold_litres} onChange={(e) => setPodForm({...podForm, low_threshold_litres: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-threshold" />
              <textarea placeholder="Notes…" value={podForm.notes} onChange={(e) => setPodForm({...podForm, notes: e.target.value})} rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreatePod(false)} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-md font-semibold">Cancel</button>
              <button onClick={createPod} disabled={saving} className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold" data-testid="fp-create-submit">
                {saving ? "Saving…" : "Create Pod"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEventFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-md">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between">
              <h2 className="text-xl font-bold">{eventForm.event_type === "refill" ? "Refill Pod" : "Dispense Fuel"}</h2>
              <button onClick={() => setShowEventFor(null)}><X className="w-6 h-6 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <input type="number" step="0.1" placeholder="Litres" value={eventForm.litres} onChange={(e) => setEventForm({...eventForm, litres: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-event-litres" />
              {eventForm.event_type === "dispense" && (
                <select value={eventForm.machine_id} onChange={(e) => setEventForm({...eventForm, machine_id: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="fp-event-machine">
                  <option value="">-- Machine fueled (optional) --</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.rego || m.name}</option>)}
                </select>
              )}
              <textarea placeholder="Notes…" value={eventForm.notes} onChange={(e) => setEventForm({...eventForm, notes: e.target.value})} rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3">
              <button onClick={() => setShowEventFor(null)} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-md font-semibold">Cancel</button>
              <button onClick={() => createEvent(showEventFor)} disabled={saving} className={`flex-1 px-4 py-3 ${eventForm.event_type === "refill" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-500 hover:bg-orange-600"} disabled:opacity-50 text-white rounded-md font-semibold`} data-testid="fp-event-submit">
                {saving ? "Saving…" : eventForm.event_type === "refill" ? "Log Refill" : "Log Dispense"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelPodsPage;
